import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { DEMO_BILLING_NOTE, useIsDemo } from '../account/beta.ts';
import { entitlementFor, spendable, useAccount, walletFor } from '../account/store.ts';
import { buyCreditPack, checkoutPlan, openBillingPortal } from '../plans/billing.ts';
import { formatAmount, PLAN_ORDER, planName, trialEligible } from '../plans/features.ts';
import { PlanSummary, rolloverText } from '../plans/PlanCard.tsx';
import { loadPlans, usePlans } from '../plans/store.ts';
import { TrialOffer } from '../plans/TrialOffer.tsx';
import { Button } from '../ui/Button.tsx';
import { IconButton } from '../ui/IconButton.tsx';
import { Segmented } from '../ui/Segmented.tsx';

/** Plan table from GET /v1/plans (never hard-coded prices; docs/spec/08). */
export function PlansScreen() {
  const navigate = useNavigate();
  const { session, me } = useAccount(useShallow((s) => ({ session: s.session, me: s.me })));
  const { catalog, error } = usePlans(useShallow((s) => ({ catalog: s.catalog, error: s.error })));
  const [interval, setBilling] = useState<'month' | 'year'>('year');
  const [busy, setBusy] = useState<string | null>(null);
  const demo = useIsDemo();
  const personal = entitlementFor(me, me?.personalScope ?? null);
  const current = personal?.plan ?? 'free';
  const wallet = walletFor(me, me?.personalScope ?? null);

  useEffect(() => {
    void loadPlans(true);
  }, []);

  const plans = [...(catalog?.plans ?? [])].sort(
    (a, b) => PLAN_ORDER.indexOf(a.id as never) - PLAN_ORDER.indexOf(b.id as never),
  );

  return (
    <main
      className="min-h-dvh"
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'calc(var(--safe-bottom) + 32px)' }}
    >
      <div className="flex min-h-11 items-center gap-2 px-2">
        <IconButton
          icon={ArrowLeft}
          label="Back"
          onClick={() => (history.length > 1 ? history.back() : void navigate({ to: '/' }))}
        />
        <h1 className="t-title">Plans</h1>
      </div>
      <div className="mx-auto flex max-w-[1240px] flex-col gap-6 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="t-body text-dim">
            {session ? `You’re on ${planName(current, catalog)}.` : 'Sign in to subscribe.'}
            {wallet ? ` ${spendable(wallet)} AI credits left.` : ''}
            {wallet?.rollover ? ` ${rolloverText(wallet.rollover, wallet.rolloverExpiresAt)}` : ''}
            {personal?.trialEndsAt ? ` Trial ends ${new Date(personal.trialEndsAt).toLocaleDateString()}.` : ''}
          </p>
          <div className="flex items-center gap-2">
            {session && personal?.source === 'stripe' && !demo ? (
              <Button size="sm" variant="ghost" onClick={() => void openBillingPortal()}>
                Manage billing
              </Button>
            ) : null}
            <Segmented
              label="Billing interval"
              size="sm"
              className="w-[220px]"
              value={interval}
              onChange={setBilling}
              options={[
                { value: 'month', label: 'Monthly' },
                { value: 'year', label: 'Yearly' },
              ]}
            />
          </div>
        </div>
        {demo ? (
          <p className="t-caption text-dim" data-testid="demo-note">
            {DEMO_BILLING_NOTE}
          </p>
        ) : null}
        {!catalog ? (
          <p className="t-body text-dim">{error ? 'Plans can’t be loaded right now.' : 'Loading plans…'}</p>
        ) : null}
        {session ? (
          <div className="max-w-[420px]">
            <TrialOffer />
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-4 min-[700px]:grid-cols-2 min-[1100px]:grid-cols-5">
          {plans.map((p) => {
            const isCurrent = session !== null && p.id === current;
            return (
              <section
                key={p.id}
                aria-label={`${p.name} plan`}
                className={`flex flex-col gap-4 rounded-xl border bg-surface p-5 ${p.featured ? 'border-accent' : 'border-border'}`}
              >
                {isCurrent ? (
                  <span className="t-caption self-start rounded-full bg-accent-soft px-2 py-0.5 text-accent">
                    Current
                  </span>
                ) : p.featured ? (
                  <span className="t-caption self-start rounded-full bg-accent px-2 py-0.5 text-accent-ink">
                    Most popular
                  </span>
                ) : null}
                {p.description ? <p className="t-caption text-dim">{p.description}</p> : null}
                <PlanSummary plan={p} interval={interval} catalog={catalog} trialEligible={trialEligible(me)} />
                <div className="mt-auto">
                  {p.id === 'free' ? (
                    <Button full disabled>
                      {current === 'free' ? 'Included' : 'Free'}
                    </Button>
                  ) : p.seats.perSeat ? (
                    <Button full onClick={() => void navigate({ to: '/team/new' })}>
                      Start {p.name}
                    </Button>
                  ) : (
                    <Button
                      full
                      variant={p.featured ? 'primary' : 'secondary'}
                      loading={busy === p.id}
                      disabled={!!busy || isCurrent || demo}
                      onClick={async () => {
                        setBusy(p.id);
                        await checkoutPlan(p.id, interval);
                        setBusy(null);
                      }}
                    >
                      Get {p.name}
                    </Button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
        {catalog?.creditPacks.length ? (
          <section className="flex flex-col gap-3" aria-label="Credit packs">
            <h2 className="t-heading">AI credit packs</h2>
            <p className="t-caption text-dim">
              One credit = one AI image. Packs never expire and work on every plan, including Free.
            </p>
            <div className="flex flex-wrap gap-2">
              {catalog.creditPacks.map((pk) => (
                <Button
                  key={pk.id}
                  loading={busy === pk.id}
                  disabled={!!busy || demo}
                  onClick={async () => {
                    setBusy(pk.id);
                    await buyCreditPack(pk.id);
                    setBusy(null);
                  }}
                >
                  {pk.credits} credits · {formatAmount(pk.amount, catalog.currency)}
                </Button>
              ))}
            </div>
          </section>
        ) : null}
        <p className="t-caption text-muted">
          Payments are handled by Stripe. App Store and Google Play subscriptions show up here automatically.
        </p>
      </div>
    </main>
  );
}
